import React, { useState } from "react";
import { get } from "lodash";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Link from "@amzn/meridian/link";

import { renderInput } from "./inputUtility";
import { formatInputWithPrefixAndSuffix } from "../../../utils/formattingUtils";
import { ImageSheet } from "./ImageSheet";

interface ISpaceGridProps {
    title?: string;
    mapping: any[];
    handleInputChange: (key: string, value: any) => void;
    isEditing: boolean;
    backgroundColor?: string;
    spacingInset?: string;
    data?: any;
    spacing?: string;
    customHeader?: string;
}

interface TableColumnProps {
    column: any;
    data: any;
    isEditing: boolean;
    handleInputChange: (key: string, value: any) => void;
    displayImages: { [key: string]: boolean };
    handleDisplayImage: (imageKey: string, imageStatus: boolean) => void;
}

const TableColumn: React.FC<TableColumnProps> = ({
    column,
    data,
    isEditing,
    handleInputChange,
    displayImages,
    handleDisplayImage,
}) => (
    <Box>
        {column.title && (
            <>
                <Text type="h200">
                    {column.title === "Ignore" ? <Box height="24px" /> : column.title}
                </Text>
                <Box height="10px" />
            </>
        )}
        <Table showDividers={true} spacing={"small"}>
            {column.options.map((item, index) => (
                <TableRow key={index}>
                    <TableCell backgroundColor="secondary" width={"50%"}>
                        <Text type={item.headerType || "b300"}>{item.header}</Text>
                        {item.imageKey && (
                            <span style={{ textDecoration: "underline" }}>
                                <Link onClick={() => handleDisplayImage(item.imageKey, true)}>
                                    <Text type="b200" color="secondary">
                                        {get(data, `${item.imageKey}.header`, "")}
                                    </Text>
                                </Link>
                                <ImageSheet
                                    imageHeader={get(data, `${item.imageKey}.header`, "")}
                                    s3ObjectIdList={get(data, `${item.imageKey}.s3ObjectIdList`, [
                                        "",
                                    ])}
                                    displayImage={displayImages[item.imageKey]}
                                    handleDisplayImage={status =>
                                        handleDisplayImage(item.imageKey, status)
                                    }
                                />
                            </span>
                        )}
                    </TableCell>
                    <TableCell alignmentHorizontal="end" width={"50%"}>
                        {item.editable && isEditing ? (
                            renderInput(item, data, handleInputChange)
                        ) : (
                            <Text type={item.valueType || "b300"}>
                                {formatInputWithPrefixAndSuffix(
                                    get(data, item.key, ""),
                                    item.prefix,
                                    item.suffix
                                )}
                            </Text>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </Table>
    </Box>
);

export const SpaceGrid: React.FC<ISpaceGridProps> = ({
    title,
    mapping,
    handleInputChange,
    isEditing,
    backgroundColor,
    spacingInset,
    data,
    spacing,
    customHeader,
}) => {
    const [displayImages, setDisplayImages] = useState<{ [key: string]: boolean }>({});

    const handleDisplayImage = (imageKey: string, imageStatus: boolean) => {
        setDisplayImages(prevState => ({
            ...prevState,
            [imageKey]: imageStatus,
        }));
    };

    return (
        <Box>
            {title && (
                <>
                    <Box height="20px" />
                    <Text type="h300">{title}</Text>
                    <Box height="20px" />
                </>
            )}
            {customHeader && (
                <Row width="100%" widths={["fill", "fill"]}>
                    <Column alignmentHorizontal="end" spacing="none">
                        <Text type="h100">{customHeader}</Text>
                        <Box height="10px" />
                    </Column>
                    <Column alignmentHorizontal="end" spacing="none">
                        <Text type="h100">{customHeader}</Text>
                        <Box height="10px" />
                    </Column>
                </Row>
            )}
            <Row
                width="100%"
                widths={mapping.map(() => `${100 / mapping.length}%`)}
                backgroundColor={backgroundColor}
                alignmentVertical="top"
                spacingInset={spacingInset}
                spacing={spacing || "none"}
            >
                {mapping.map((column, index) => (
                    <TableColumn
                        key={index}
                        column={column}
                        data={data}
                        isEditing={isEditing}
                        handleInputChange={handleInputChange}
                        displayImages={displayImages}
                        handleDisplayImage={handleDisplayImage}
                    />
                ))}
            </Row>
        </Box>
    );
};
